// @ts-nocheck
import { useState } from "react";
import type { ReactNode } from "react";
import {
  Button, Modal, Drawer, Form, Input, InputNumber, Switch,
  Typography, Tag, App, Dropdown,
} from "../components/admin-ui";
import {
  EditOutlined, PlusOutlined, DeleteOutlined, CloseOutlined, MoreOutlined,
  ShopOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import type { Stall } from "@kawkaw/shared";
import { DataTable } from "../components/DataTable";
import { ImageUpload } from "../components/ImageUpload";
import { AdminShell } from "../layouts/AdminShell";
import { tokens } from "../styles/tokens";
import { useAdminStalls, useUpdateStall, useCreateStall, useDeleteStall } from "../api/hooks";

const { Title } = Typography;
const { color: T } = tokens;
const C = {
  brand: T.brand,
  textPrimary: T.textDark,
  textMuted: T.textMuted,
};

// ─── Section header used in the Add Stall modal ─────────────────────────────
function SectionHeader({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div className="admin-stalls-section-heading" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, marginTop: 4 }}>
      <div className="admin-stalls-section-icon" style={{
        width: 28, height: 28, borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14,
      }}>
        {icon}
      </div>
      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: C.textPrimary, textTransform: "uppercase" }}>
        {label}
      </span>
    </div>
  );
}

function StallStatCard({
  label, value, icon, iconColor, badge, badgeColor, badgeBg,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  iconColor: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
}) {
  return (
    <div className="admin-kpi-card admin-stalls-stat">
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon" style={{ color: iconColor, background: badgeBg }}>{icon}</span>
      </div>
      <div className="admin-kpi-value">{value.toLocaleString()}</div>
      <div className="admin-stalls-stat-note" style={{ color: badgeColor, background: badgeBg }}>{badge}</div>
    </div>
  );
}

// ─── Add Stall Drawer (Figma 57-1546) ───────────────────────────────────────
type AddStallValues = {
  name: string;
  description?: string | null;
  cover_image?: string | null;
  is_open: boolean;
  sort_order: number;
  sst_percent?: number | null;
};

/** The form edits SST as a percentage; the API stores a fraction (null = follow the Kopitiam-wide rate). */
const sstFraction = (percent: number | null | undefined) => (percent == null ? null : Math.round(percent * 100) / 10000);

function AddStallModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<AddStallValues>();
  const createStall = useCreateStall();

  const onFinish = async (values: AddStallValues) => {
    try {
      await createStall.mutateAsync({
        name: values.name,
        description: values.description ?? null,
        cover_image: values.cover_image ?? null,
        is_open: values.is_open,
        sort_order: values.sort_order,
        sst_rate: sstFraction(values.sst_percent),
      });
      void message.success(t("stalls.createSuccess"));
      form.resetFields();
      onClose();
    } catch {
      void message.error(t("stalls.createFailed"));
    }
  };

  const handleDiscard = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleDiscard}
      placement="right"
      width={460}
      closeIcon={null}
      title={
        <div className="admin-stalls-drawer-heading" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div className="admin-stalls-drawer-title">{t("stalls.addStall")}</div>
            <div className="admin-stalls-drawer-subtitle">{t("stalls.drawerSubtitleNew")}</div>
          </div>
          <Button aria-label="Close" className="admin-duplicate-close" type="text" icon={<CloseOutlined />} onClick={handleDiscard} />
        </div>
      }
      footer={
        <div className="admin-stalls-drawer-actions">
          <Button onClick={handleDiscard}>{t("stalls.discard")}</Button>
          <Button type="primary" loading={createStall.isPending} onClick={() => void form.submit()}>
            {t("stalls.createStall")}
          </Button>
        </div>
      }
      maskClosable={false}
      destroyOnClose
    >
      <Form
        className="admin-stalls-create-form"
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ is_open: true, sort_order: 1 }}
        colon={false}
      >
        {/* ── BASIC INFO ────────────────────────────── */}
        <SectionHeader
          icon={<span style={{ fontSize: 16, background: T.brandLight, borderRadius: "50%", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>ℹ️</span>}
          label={t("stalls.basicInfo")}
        />
        <Form.Item
          name="name"
          label={<span style={{ fontWeight: 600, fontSize: 13, color: C.textPrimary }}>{t("stalls.stallName")} <span style={{ color: T.dangerText }}>*</span></span>}
          rules={[{ required: true, message: t("stalls.stallNameRequired") }, { max: 120 }]}
          style={{ marginBottom: 16 }}
        >
          <Input
            placeholder={t("stalls.stallNamePlaceholder")}
            maxLength={120}
            style={{ borderRadius: 10, height: 44, fontSize: 14, color: T.textDark }}
          />
        </Form.Item>
        <Form.Item
          name="description"
          label={<span style={{ fontWeight: 600, fontSize: 13, color: C.textPrimary }}>{t("stalls.description")}</span>}
          style={{ marginBottom: 24 }}
        >
          <Input.TextArea
            placeholder={t("stalls.descriptionPlaceholder")}
            maxLength={400}
            rows={3}
            style={{ borderRadius: 10, fontSize: 14, resize: "none", color: T.textDark }}
          />
        </Form.Item>
        <Form.Item
          className="admin-stalls-cover-upload"
          name="cover_image"
          label={<span style={{ fontWeight: 600, fontSize: 13, color: C.textPrimary }}>{t("stalls.formCoverImage")}</span>}
          style={{ marginBottom: 24 }}
        >
          <ImageUpload folder="stalls" hint={t("imageUpload.stallHint")} />
        </Form.Item>

        <div className="admin-stalls-form-divider" style={{ borderTop: "1px solid #F3F4F6", marginBottom: 20 }} />

        {/* ── STATUS ────────────────────────────────── */}
        <SectionHeader
          icon={<span style={{ fontSize: 16, background: T.surfaceGreen, borderRadius: "50%", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>⚡</span>}
          label={t("stalls.status")}
        />
        <div className="admin-stalls-status-card" style={{
          background: T.bg, borderRadius: 12,
          padding: "14px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 24,
        }}>
          <div className="admin-stalls-status-copy">
            <div style={{ fontWeight: 700, fontSize: 14, color: T.textDark }}>{t("stalls.stallStatus")}</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, maxWidth: 230 }}>
              {t("stalls.stallStatusHint")}
            </div>
          </div>
          <Form.Item name="is_open" valuePropName="checked" noStyle>
            <Switch />
          </Form.Item>
        </div>

        <div className="admin-stalls-form-divider" style={{ borderTop: "1px solid #F3F4F6", marginBottom: 20 }} />

        {/* ── DISPLAY ORDER ─────────────────────────── */}
        <SectionHeader
          icon={<span style={{ fontSize: 16, background: T.surfaceBlue, borderRadius: "50%", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>☰</span>}
          label={t("stalls.displayOrder")}
        />
        <Form.Item className="admin-stalls-sort-field" name="sort_order" style={{ marginBottom: 4 }}>
          <InputNumber
            min={0}
            style={{ width: 120, borderRadius: 10, height: 44 }}
          />
        </Form.Item>
        <Form.Item name="sst_percent" label={t("stalls.formSst")} extra={t("stalls.formSstHint")} style={{ marginTop: 16, marginBottom: 4 }}>
          <InputNumber min={0} max={100} step={0.5} addonAfter="%" placeholder={t("stalls.sstFollowGlobal")} style={{ width: 180 }} />
        </Form.Item>
        <div className="admin-stalls-field-hint" style={{ fontSize: 12, color: T.textMuted, marginBottom: 24 }}>
          {t("stalls.displayOrderHint")}
        </div>

        <div className="admin-stalls-form-divider" style={{ borderTop: "1px solid #F3F4F6", marginBottom: 20 }} />

        {/* ── OPENING HOURS ─────────────────────────── */}
        <div className="admin-stalls-hours-title" style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textDark, textTransform: "uppercase", marginBottom: 14 }}>
          {t("stalls.openingHours")}
        </div>
        <div className="admin-stalls-hours-row" style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: T.textDark }}>{t("stalls.weekdaysHours")}</span>
          <span style={{ fontSize: 14, color: T.textBody }}>{t("stalls.hoursWeekdayExample")}</span>
        </div>
        <div className="admin-stalls-hours-row" style={{ display: "flex", justifyContent: "space-between", marginBottom: 28 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: T.textDark }}>{t("stalls.weekendHours")}</span>
          <span style={{ fontSize: 14, color: T.textBody }}>{t("stalls.hoursWeekendExample")}</span>
        </div>
      </Form>
    </Drawer>
  );
}

// ─── Main screen ─────────────────────────────────────────────────────────────

export function StallsScreen() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Stall | null>(null);
  const [editForm] = Form.useForm<Partial<Stall>>();

  const { data, isLoading } = useAdminStalls();
  const updateStall = useUpdateStall();
  const deleteStall = useDeleteStall();
  const stalls = (data?.data ?? []) as Stall[];
  const openCount = stalls.filter(stall => stall.is_open).length;
  const pausedCount = stalls.length - openCount;

  const openEdit = (s: Stall) => {
    setEditing(s);
    editForm.setFieldsValue({
      name: s.name,
      description: s.description,
      sort_order: s.sort_order,
      cover_image: s.cover_image,
      is_open: s.is_open,
      sst_percent: s.sst_rate != null ? Math.round(Number(s.sst_rate) * 10000) / 100 : null,
    });
    setEditOpen(true);
  };

  const onSave = async (values: Partial<Stall> & { sst_percent?: number | null }) => {
    if (!editing) return;
    try {
      const { sst_percent, ...rest } = values;
      await updateStall.mutateAsync({ id: editing.id, ...rest, sst_rate: sstFraction(sst_percent) });
      void message.success(t("stalls.updateSuccess"));
      setEditOpen(false);
    } catch {
      void message.error(t("stalls.saveFailed"));
    }
  };

  const toggleOpen = async (row: Stall, next: boolean) => {
    try {
      await updateStall.mutateAsync({ id: row.id, is_open: next });
      void message.success(next ? t("stalls.opened") : t("stalls.closed"));
    } catch {
      void message.error(t("stalls.saveFailed"));
    }
  };

  const confirmDelete = (row: Stall) => {
    Modal.confirm({
      title: t("stalls.deleteConfirm"),
      okText: t("common.yes"),
      cancelText: t("common.no"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteStall.mutateAsync(row.id);
          void message.success(t("stalls.deleteSuccess"));
        } catch {
          void message.error(t("stalls.deleteFailed"));
        }
      },
    });
  };

  const columns: ColumnsType<Stall> = [
    {
      title: t("stalls.colCover"), dataIndex: "cover_image", key: "cover", width: 80,
      render: (v?: string | null, row?: Stall) => v
        ? <img className="admin-stalls-cover" src={v} alt={row?.name ?? ""} />
        : <span className="admin-stalls-cover-placeholder">{row?.name?.slice(0, 1).toUpperCase() ?? "—"}</span>,
    },
    {
      title: t("stalls.colName"), dataIndex: "name", key: "name",
      render: (value: string) => <span className="admin-stalls-name">{value}</span>,
    },
    {
      title: t("stalls.colDescription"), dataIndex: "description", key: "description",
      render: (v?: string | null) => <span className="admin-stalls-description">{v ?? "—"}</span>,
    },
    {
      title: t("stalls.colSortOrder"), dataIndex: "sort_order", key: "sort_order", width: 70,
      render: (v: number) => <Tag className="admin-stalls-sort">{v}</Tag>,
    },
    {
      title: t("stalls.colSst"), dataIndex: "sst_rate", key: "sst_rate", width: 90,
      render: (v?: string | null) => <Tag className="admin-stalls-sort">{v != null ? `${Math.round(Number(v) * 10000) / 100}%` : t("stalls.sstFollowGlobal")}</Tag>,
    },
    {
      title: t("stalls.colOpen"), dataIndex: "is_open", key: "is_open", width: 100,
      render: (v: boolean, row) => (
        <span className="admin-stalls-open-control">
          <Switch checked={v} onChange={(checked) => void toggleOpen(row, checked)} size="small" />
        </span>
      ),
    },
    {
      title: t("stalls.colActions"), key: "actions", width: 96, fixed: "right",
      render: (_, row) => (
        <Dropdown
          menu={{
            items: [
              { key: "edit", icon: <EditOutlined />, label: t("common.edit"), onClick: () => openEdit(row) },
              { key: "delete", icon: <DeleteOutlined />, label: t("common.delete"), danger: true, onClick: () => confirmDelete(row) },
            ],
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            className="admin-stalls-more admin-data-more"
            type="text"
            aria-label={`${t("stalls.colActions")} ${row.name}`}
            icon={<MoreOutlined style={{ fontSize: 18 }} />}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="admin-stalls-page admin-list-page">
        <div style={{ maxWidth: 1480, margin: "0 auto" }}>
          <div className="admin-stalls-toolbar">
            <div>
              <h1 className="admin-page-title">{t("stalls.title")}</h1>
              <p className="admin-stalls-subtitle">{t("stalls.pageSubtitle")}</p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddOpen(true)}
              className="admin-stalls-create"
            >
              {t("stalls.addStall")}
            </Button>
          </div>

          <div className="admin-stalls-stats grid">
            <StallStatCard
              label={t("stalls.statTotal")} value={stalls.length}
              icon={<ShopOutlined />} iconColor={T.brand}
              badge={t("stalls.statBadgeOverall")} badgeColor={T.brandAmberText} badgeBg={T.warningBg}
            />
            <StallStatCard
              label={t("stalls.statOpen")} value={openCount}
              icon={<CheckCircleOutlined />} iconColor={T.successBright}
              badge={t("stalls.statBadgeOpen")} badgeColor={T.successText} badgeBg={T.successBg}
            />
            <StallStatCard
              label={t("stalls.statPaused")} value={pausedCount}
              icon={<CloseCircleOutlined />} iconColor={T.dangerText}
              badge={t("stalls.statBadgePaused")} badgeColor={T.dangerText} badgeBg={T.dangerBg}
            />
          </div>

          <p className="admin-stalls-results">{t("common.total", { count: stalls.length })}</p>
          <div className="admin-stalls-table-panel admin-data-table-panel">
            <DataTable<Stall> columns={columns} dataSource={stalls} loading={isLoading} />
          </div>
        </div>
      </div>

      {/* Add Stall Drawer */}
      <AddStallModal open={addOpen} onClose={() => setAddOpen(false)} />

      {/* Edit Stall Modal */}
      <Modal
        open={editOpen}
        title={<div className="admin-stalls-edit-title">{t("stalls.editTitle")} — {editing?.name ?? ""}</div>}
        onCancel={() => setEditOpen(false)}
        onOk={() => void editForm.submit()}
        okButtonProps={{ style: { background: T.brand, borderColor: T.brand }, loading: updateStall.isPending }}
      >
        <Form className="admin-stalls-edit-form" form={editForm} layout="vertical" onFinish={onSave} colon={false}>
          <Form.Item name="name" label={t("stalls.formName")} rules={[{ required: true, max: 120 }]}>
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item name="description" label={t("stalls.formDescription")}>
            <Input.TextArea maxLength={400} rows={3} showCount />
          </Form.Item>
          <Form.Item name="cover_image" label={t("stalls.formCoverImage")}>
            <ImageUpload folder="stalls" hint={t("imageUpload.stallHint")} />
          </Form.Item>
          <Form.Item name="sort_order" label={t("stalls.formSortOrder")}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="sst_percent" label={t("stalls.formSst")} extra={t("stalls.formSstHint")}>
            <InputNumber min={0} max={100} step={0.5} addonAfter="%" placeholder={t("stalls.sstFollowGlobal")} style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="is_open" label={t("stalls.formIsOpen")} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </AdminShell>
  );
}
